import {Card, Rank, Separator, Trend} from 'platform/components';
import {
  Align,
  Box,
  Center,
  CSSDimension,
  getSize,
  Heading,
  Hide,
  Space,
  Stack,
  Text,
  ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {useFormatPercentage} from 'platform/locale';
import {match} from 'ts-pattern';

import {useCallback, useMemo} from 'react';
import {Trans} from 'react-i18next';

import {isNil, isNotNil, sort} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Alert} from '../../../../components/Alert/Alert';
import {AvoidBreakInside} from '../../../../components/AvoidBreakInside/AvoidBreakInside';
import {useCatalogue} from '../../../../hooks/useCatalogue';
import i18n from '../../../../i18n/i18n';
import {MarketType} from '../../../../types/MarketType';
import {PriceReportType} from '../../../../types/PriceReportType';
import {getDifference} from '../../../../utils/getDifference';
import {useFeatureCompare} from '../../../../utils/useFeatureCompare';
import {ResponsiveGrid} from '../../components/ResponsiveGrid';
import {useIsPrintForced} from '../../hooks/useIsPrintForced';
import {getDiffLabel} from '../../utils/getDiffLabel';
import {getValueOrEmptyString} from '../../utils/getValueOrEmptyString';
import {AvailableFeatures} from './components/AvailableFeatures';
import {MissingFeatures} from './components/MissingFeatures';
import {getStatisticVariant} from './utils/getStatisticVariant';

const PERCENTAGE_MULTIPLIER = 100;

interface StatisticsCardFeaturesProps extends TestIdProps {
  market?: MarketType;
  priceReport: PriceReportType;
}

export function StatisticsCardFeatureLevel(props: StatisticsCardFeaturesProps) {
  const {isPrint, getResponsiveValue} = useIsPrintForced();
  const formatPercentage = useFormatPercentage();
  const {getFeatureLabel, hasFeatureTranslation} = useCatalogue();
  const featureCompare = useFeatureCompare();

  const variant = getStatisticVariant(
    props.priceReport.featuresLevel ?? 0,
    props.market?.sold?.averageFeatureLevel ?? 0
  );

  const alertTranslationKey = match(variant)
    .with('success', () => 'entity.overviewStatistics.labels.featuresLevelAlertSuccess')
    .with('neutral', () => 'entity.overviewStatistics.labels.featuresLevelAlertNeutral')
    .with('warning', () => 'entity.overviewStatistics.labels.featuresLevelAlertWarning')
    .exhaustive();

  const alertIcon = match<string | undefined, ThemeIconKey | undefined>(variant)
    .with('success', () => 'action/trending_up')
    .with('warning', () => 'action/trending_down')
    .otherwise(() => undefined);

  const isSomeVehicleSold = (props.market?.sold?.similarVehicle ?? 0) > 0;
  const isSomeVehicleInStock = (props.market?.stock?.similarVehicle ?? 0) > 0;

  const featuresLevel = props.priceReport.featuresLevel;
  const featureLevelSold = isSomeVehicleSold ? props.market?.sold?.averageFeatureLevel : undefined;
  const featureLevelStock = isSomeVehicleInStock
    ? props.market?.stock?.averageFeatureLevel
    : undefined;

  const soldDifference = getDifference(featuresLevel, featureLevelSold, 100);

  const soldDifferenceAbsolute = isNil(soldDifference) ? undefined : Math.abs(soldDifference);

  const stockDifference = getDifference(featuresLevel, featureLevelStock, 100);

  const mapFeatureKeysToTranslations = useCallback(
    (features: string[]) => features.filter(hasFeatureTranslation).map(getFeatureLabel),
    [hasFeatureTranslation, getFeatureLabel]
  );

  const availableFeatures = useMemo(
    () =>
      mapFeatureKeysToTranslations(
        sort(featureCompare, props.market?.recommendedFeatures?.available ?? [])
      ),
    [mapFeatureKeysToTranslations, featureCompare, props.market?.recommendedFeatures?.available]
  );
  const missingFeatures = useMemo(
    () =>
      mapFeatureKeysToTranslations(
        sort(featureCompare, props.market?.recommendedFeatures?.missing ?? [])
      ),
    [mapFeatureKeysToTranslations, featureCompare, props.market?.recommendedFeatures?.missing]
  );

  const featuresBoxWidth: CSSDimension = getResponsiveValue(
    '100%',
    '100%',
    `calc(50% - ${getSize(4)})` as const,
    `calc(50% - ${getSize(4)})` as const,
    `calc(50% - ${getSize(4)})` as const
  );

  return (
    <AvoidBreakInside>
      <Card variant={isPrint ? 'inlineWhite' : undefined} data-testid={props['data-testid']}>
        <Box padding={2}>
          <VStack spacing={6}>
            <Align center>
              <Heading size={3} data-testid={suffixTestId('featuresTitle', props)}>
                {i18n.t('entity.overviewStatistics.labels.featuresTitle')}
              </Heading>
            </Align>
            <Text
              align="center"
              size="small"
              color="secondary"
              data-testid={suffixTestId('featuresDescription', props)}
            >
              <Trans
                i18nKey="entity.overviewStatistics.labels.featuresDesciption"
                components={{bold: <Text alternative inline size="small" />}}
              />
            </Text>
            <ResponsiveGrid>
              <Box>
                <Align center>
                  <Text
                    size="xSmall"
                    color="secondary"
                    data-testid={suffixTestId('featuresYourVehicle', props)}
                  >
                    {i18n.t('entity.overviewStatistics.labels.featureLevelYourVehicle')}
                  </Text>
                </Align>
                <Space vertical={2} />
                <Center spacing={2}>
                  <Heading size={2} data-testid={suffixTestId('vehicleFeatureLevel', props)}>
                    {getValueOrEmptyString(featuresLevel, (value) =>
                      formatPercentage(value, 'down')
                    )}
                  </Heading>
                  {isNotNil(featuresLevel) && (
                    <Rank
                      percentage={featuresLevel * PERCENTAGE_MULTIPLIER}
                      data-testid={suffixTestId('featureLevelRank', props)}
                    />
                  )}
                </Center>
              </Box>
              <Box>
                <VStack spacing={2} align="center">
                  <Text
                    size="xSmall"
                    color="secondary"
                    data-testid={suffixTestId('featuresSoldCars', props)}
                  >
                    {i18n.t('entity.overviewStatistics.labels.featureLevelSoldCars')}
                  </Text>
                  <Center spacing={2}>
                    <Heading size={2} data-testid={suffixTestId('soldAverageFeatureLevel', props)}>
                      {getValueOrEmptyString(featureLevelSold, (value) =>
                        formatPercentage(value, 'down')
                      )}
                    </Heading>
                    {isNotNil(featureLevelSold) && (
                      <Rank
                        percentage={featureLevelSold * PERCENTAGE_MULTIPLIER}
                        data-testid={suffixTestId('soldFeatureLevelRank', props)}
                      />
                    )}
                  </Center>
                  {isNotNil(soldDifference) && (
                    <Trend
                      label={getDiffLabel(soldDifference, (value: number) =>
                        formatPercentage(value, 'nearest')
                      )}
                      isSubtle
                      isSmall
                      isPositive={soldDifference > 0}
                      isNegative={soldDifference < 0}
                      data-testid={suffixTestId('soldFeatureLevelTrend', props)}
                    />
                  )}
                </VStack>
              </Box>
              <VStack spacing={2} align="center">
                <Text
                  size="xSmall"
                  color="secondary"
                  data-testid={suffixTestId('featuresStockCars', props)}
                >
                  {i18n.t('entity.overviewStatistics.labels.featureLevelStockCars')}
                </Text>
                <Center spacing={2}>
                  <Heading size={2} data-testid={suffixTestId('stockAverageFeatureLevel', props)}>
                    {getValueOrEmptyString(featureLevelStock, (value) =>
                      formatPercentage(value, 'down')
                    )}
                  </Heading>
                  {isNotNil(featureLevelStock) && (
                    <Rank
                      percentage={featureLevelStock * PERCENTAGE_MULTIPLIER}
                      data-testid={suffixTestId('stockFeatureLevelRank', props)}
                    />
                  )}
                </Center>
                {isNotNil(stockDifference) && (
                  <Trend
                    label={getDiffLabel(stockDifference, (value: number) =>
                      formatPercentage(value, 'nearest')
                    )}
                    isSubtle
                    isSmall
                    isPositive={stockDifference > 0}
                    isNegative={stockDifference < 0}
                    data-testid={suffixTestId('stockFeatureLevelTrend', props)}
                  />
                )}
              </VStack>
            </ResponsiveGrid>
            <Hide
              when={
                isNil(props.priceReport.featuresLevel) ||
                isNil(props.market?.sold?.averageFeatureLevel) ||
                !isSomeVehicleSold
              }
            >
              <Alert
                variant={variant === 'warning' ? 'warning' : 'success'}
                icon={alertIcon}
                data-testid={suffixTestId('featuresAlert', props)}
              >
                <Trans
                  i18nKey={alertTranslationKey}
                  values={{
                    percentage: getValueOrEmptyString(soldDifferenceAbsolute, (value) =>
                      formatPercentage(value, 'nearest')
                    ),
                  }}
                  components={{bold: <Text alternative inline size="small" />}}
                />
              </Alert>
            </Hide>
            <Hide
              when={
                isNilOrEmpty(props.market?.recommendedFeatures?.available) &&
                isNilOrEmpty(props.market?.recommendedFeatures?.missing)
              }
            >
              <Stack direction={getResponsiveValue('column', 'column', 'row', 'row', 'row')}>
                <Box flex={1} maxWidth={featuresBoxWidth}>
                  <AvailableFeatures
                    features={availableFeatures}
                    data-testid={suffixTestId('availableFeatures', props)}
                  />
                </Box>
                <Separator
                  orientation={getResponsiveValue(
                    'horizontal',
                    'horizontal',
                    'vertical',
                    'vertical',
                    'vertical'
                  )}
                  spacing={getResponsiveValue(6, 6, 4, 4, 4)}
                />
                <Box flex={1} maxWidth={featuresBoxWidth}>
                  <MissingFeatures
                    features={missingFeatures}
                    data-testid={suffixTestId('missingFeatures', props)}
                  />
                </Box>
              </Stack>
            </Hide>
          </VStack>
        </Box>
      </Card>
    </AvoidBreakInside>
  );
}
